import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Users,
  UserCheck,
  ShieldAlert,
  Activity,
  Settings2,
  ScrollText,
  KeyRound,
  Lock,
  Save,
  BadgeCheck,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  FileText,
  Wallet,
  Ticket,
  Megaphone,
  ImagePlus,
  CalendarClock,
} from "lucide-react";
import AppShell from "../../components/AppShell";
import { Alert, Spinner } from "../../components/ui";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function AdminDashboard() {
  const [params] = useSearchParams();
  const tab = params.get("tab") || "overview";
  return (
    <AppShell>
      {tab === "audit" ? <AuditTab /> : tab === "config" ? <ConfigTab /> : tab === "disputes" ? <DisputesTab /> : tab === "skck" ? <SkckTab /> : tab === "finance" ? <FinanceTab /> : tab === "promo" ? <PromoTab /> : tab === "banner" ? <BannerTab /> : <OverviewTab />}
    </AppShell>
  );
}

function TabNav() {
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
      <Link to="/admin" className="chip bg-brand-50 text-brand-600 hover:bg-brand-100 whitespace-nowrap">
        <Activity className="h-4 w-4" /> Overview
      </Link>
      <Link to="/admin/kyc" className="chip bg-amber-50 text-amber-600 hover:bg-amber-100 whitespace-nowrap">
        <BadgeCheck className="h-4 w-4" /> Review KYC
      </Link>
      <Link to="/admin?tab=skck" className="chip bg-orange-50 text-orange-600 hover:bg-orange-100 whitespace-nowrap">
        <FileText className="h-4 w-4" /> SKCK
      </Link>
      <Link to="/admin?tab=disputes" className="chip bg-red-50 text-red-600 hover:bg-red-100 whitespace-nowrap">
        <AlertTriangle className="h-4 w-4" /> Komplain
      </Link>
      <Link to="/admin?tab=finance" className="chip bg-emerald-50 text-emerald-600 hover:bg-emerald-100 whitespace-nowrap">
        <Wallet className="h-4 w-4" /> Keuangan
      </Link>
      <Link to="/admin?tab=promo" className="chip bg-violet-50 text-violet-600 hover:bg-violet-100 whitespace-nowrap">
        <Ticket className="h-4 w-4" /> Promo
      </Link>
      <Link to="/admin?tab=banner" className="chip bg-pink-50 text-pink-600 hover:bg-pink-100 whitespace-nowrap">
        <Megaphone className="h-4 w-4" /> Banner
      </Link>
    </div>
  );
}

function TabHeader({ title, sub }) {
  return (
    <div className="mb-6">
      <h1 className="text-2xl md:text-3xl text-brand-800">{title}</h1>
      <p className="text-brand-400 mt-1">{sub}</p>
    </div>
  );
}

function OverviewTab() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/api/admin/overview").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e.response?.data?.detail)));
  }, []);

  if (err) return <AppErr err={err} />;
  if (!data) return <Spinner />;

  const cards = [
    { label: "Pelanggan", value: data.counts.customers, icon: Users },
    { label: "Pekerja", value: data.counts.workers, icon: UserCheck },
    { label: "Admin", value: data.counts.admins, icon: ShieldAlert },
    { label: "KYC Menunggu", value: data.kyc_pending, icon: Activity },
  ];

  return (
    <div className="animate-fade-up">
      <TabNav />
      <TabHeader title="Dashboard Admin" sub={`Masuk sebagai ${user.admin_role} • kontrol operasional & keamanan`} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="card p-5" data-testid={`admin-stat-${c.label}`}>
            <c.icon className="h-6 w-6 text-brand-600" />
            <p className="text-3xl font-extrabold text-brand-800 mt-3">{c.value}</p>
            <p className="text-sm text-brand-400">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="h-5 w-5 text-brand-600" />
            <h2 className="text-xl text-brand-800">Izin Anda (RBAC)</h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="admin-permissions">
            {data.your_permissions.map((p) => (
              <span key={p} className="chip bg-brand-50 text-brand-700 font-mono text-xs">{p}</span>
            ))}
          </div>
          <p className="text-xs text-brand-400 mt-4 flex items-center gap-1">
            <Lock className="h-3.5 w-3.5" /> Deny-by-default • Ledger bersifat append-only (tidak dapat diubah).
          </p>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="h-5 w-5 text-brand-600" />
            <h2 className="text-xl text-brand-800">Aktivitas terbaru</h2>
          </div>
          <ul className="space-y-2" data-testid="admin-recent-events">
            {data.recent_events.map((e, i) => (
              <li key={i} className="flex items-center justify-between text-sm border-b border-brand-50 pb-2">
                <span className="font-semibold text-brand-700">{e.event_type} · {e.action}</span>
                <span className="text-brand-300">{new Date(e.created_at).toLocaleString("id-ID")}</span>
              </li>
            ))}
            {data.recent_events.length === 0 && <li className="text-brand-400">Belum ada aktivitas.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ConfigTab() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = () =>
    api.get("/api/admin/config").then((r) => setRows(r.data.policies)).catch((e) => setErr(formatApiError(e.response?.data?.detail)));

  useEffect(() => { load(); }, []);

  if (err) return <AppErr err={err} />;
  if (!rows) return <Spinner />;

  return (
    <div className="animate-fade-up">
      <TabHeader title="Registry Konfigurasi" sub="Nilai bisnis terkunci Fase 0. Nilai TBD belum dapat digunakan." />
      {msg && <div className="mb-4"><Alert kind="success">{msg}</Alert></div>}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-brand-50 text-brand-600 text-sm">
              <tr>
                <th className="px-5 py-3 font-semibold">Kunci</th>
                <th className="px-5 py-3 font-semibold">Nilai</th>
                <th className="px-5 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody data-testid="config-table">
              {rows.map((p) => (
                <tr key={p.key} className="border-t border-brand-50">
                  <td className="px-5 py-3 font-mono text-sm text-brand-800">{p.key}</td>
                  <td className="px-5 py-3 text-sm text-brand-600">{JSON.stringify(p.value)}</td>
                  <td className="px-5 py-3">
                    <span className={`chip ${p.status === "ACTIVE" ? "bg-success-50 text-success-600" : "bg-amber-50 text-amber-600"}`}>
                      {p.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/api/admin/audit").then((r) => setLogs(r.data.logs)).catch((e) => setErr(formatApiError(e.response?.data?.detail)));
  }, []);

  if (err) return <AppErr err={err} />;
  if (!logs) return <Spinner />;

  return (
    <div className="animate-fade-up">
      <TabHeader title="Log Audit" sub="Append-only • tidak dapat diubah atau dihapus." />
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-brand-50 text-brand-600">
              <tr>
                <th className="px-4 py-3 font-semibold">Waktu</th>
                <th className="px-4 py-3 font-semibold">Tipe</th>
                <th className="px-4 py-3 font-semibold">Aksi</th>
                <th className="px-4 py-3 font-semibold">Entitas</th>
                <th className="px-4 py-3 font-semibold">Peran</th>
              </tr>
            </thead>
            <tbody data-testid="audit-table">
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-brand-50">
                  <td className="px-4 py-3 text-brand-400 whitespace-nowrap">{new Date(l.created_at).toLocaleString("id-ID")}</td>
                  <td className="px-4 py-3"><span className="chip bg-brand-50 text-brand-700">{l.event_type}</span></td>
                  <td className="px-4 py-3 font-semibold text-brand-800">{l.action}</td>
                  <td className="px-4 py-3 text-brand-600 font-mono text-xs">{l.entity}</td>
                  <td className="px-4 py-3 text-brand-600">{l.actor_role || "-"}</td>
                </tr>
              ))}
              {logs.length === 0 && <tr><td colSpan="5" className="px-4 py-6 text-center text-brand-400">Belum ada log.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AppErr({ err }) {
  return <div className="max-w-lg"><Alert testid="admin-error">{err}</Alert></div>;
}

function DisputesTab() {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [resolveNote, setResolveNote] = useState("");
  const [completionPct, setCompletionPct] = useState("50");

  useEffect(() => {
    api.get("/api/bookings/disputes")
      .then((r) => setDisputes(r.data || []))
      .catch((e) => setError(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const handleResolve = async (bookingId, action) => {
    if (!resolveNote.trim()) return;
    const pct = parseFloat(completionPct);
    if (action === "partial" && !(pct >= 0 && pct <= 100)) {
      setError("Isi % penyelesaian 0-100 untuk refund sebagian.");
      return;
    }
    setActionLoading(true);
    setError("");
    try {
      await api.post(`/api/bookings/${bookingId}/resolve-dispute`, {
        resolution: resolveNote,
        action,
        ...(action === "partial" ? { completion_pct: pct } : {}),
      });
      setDisputes((prev) => prev.filter((d) => d.id !== bookingId));
      setResolveNote("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setActionLoading(false);
    }
  };

  // Proration Phase 3.2 (mirror backend config)
  const prorationFor = (pct) => {
    if (pct >= 75) return 0;
    if (pct >= 50) return 25;
    if (pct >= 25) return 50;
    return 75;
  };

  if (loading) return <Spinner />;

  return (
    <div className="animate-fade-up">
      <TabNav />
      <TabHeader title="Komplain / Dispute" sub="Review dan selesaikan komplain dari pekerja atau pelanggan" />

      {error && <Alert kind="error" className="mb-4">{error}</Alert>}

      {disputes.length === 0 ? (
        <div className="card p-10 text-center">
          <CheckCircle2 className="h-12 w-12 text-success-400 mx-auto mb-3" />
          <p className="font-bold text-brand-800">Tidak ada komplain</p>
          <p className="text-sm text-brand-400 mt-1">Semua booking dalam kondisi normal.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {disputes.map((d) => (
            <div key={d.id} className="card p-6 border-red-200">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-bold text-brand-800">{d.service?.name || "Service"}</p>
                  <p className="text-sm text-brand-400">ID: {d.id?.slice(0, 8)}</p>
                </div>
                <span className="chip bg-red-50 text-red-600">
                  <AlertTriangle className="h-3.5 w-3.5" /> DISPUTED
                </span>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-brand-400">Pekerja</p>
                  <p className="text-sm font-medium text-brand-700">{d.worker?.display_name || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-brand-400">Pelanggan</p>
                  <p className="text-sm font-medium text-brand-700">{d.customer?.display_name || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-brand-400">Biaya</p>
                  <p className="text-sm font-medium text-brand-700">Rp{(d.quoted_amount || 0).toLocaleString("id-ID")}</p>
                </div>
                <div>
                  <p className="text-xs text-brand-400">Status Sebelum</p>
                  <p className="text-sm font-medium text-brand-700">{d.status}</p>
                </div>
              </div>

              <div className="p-3 bg-red-50 rounded-xl mb-4">
                <p className="text-xs text-red-400 mb-1">Alasan Komplain:</p>
                <p className="text-sm text-red-700">{d.dispute_reason}</p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-sm text-brand-500 mb-1 block">Catatan Resolusi</label>
                  <textarea
                    value={resolveNote}
                    onChange={(e) => setResolveNote(e.target.value)}
                    placeholder="Jelaskan keputusan Anda..."
                    rows={2}
                    className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none resize-none"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleResolve(d.id, "keep")}
                    className="btn-success flex-1"
                    disabled={actionLoading || !resolveNote.trim()}
                    data-testid={`resolve-keep-${d.id}`}
                  >
                    <CheckCircle2 className="h-4 w-4" /> Pertahankan
                  </button>
                  <button
                    onClick={() => handleResolve(d.id, "rework")}
                    className="btn flex-1 bg-amber-500 text-white hover:bg-amber-600"
                    disabled={actionLoading || !resolveNote.trim()}
                    data-testid={`resolve-rework-${d.id}`}
                  >
                    <RefreshCw className="h-4 w-4" /> Rework
                  </button>
                  <button
                    onClick={() => handleResolve(d.id, "cancel")}
                    className="btn-ghost flex-1 border-red-200 text-red-600"
                    disabled={actionLoading || !resolveNote.trim()}
                    data-testid={`resolve-cancel-${d.id}`}
                  >
                    <XCircle className="h-4 w-4" /> Batalkan
                  </button>
                </div>
                <div className="rounded-xl border-2 border-dashed border-brand-200 p-3">
                  <p className="text-xs font-bold text-brand-700 mb-1">
                    Refund Sebagian (proration: selesai ≥75%→0%, 50-74%→25%, 25-49%→50%, &lt;25%→75%)
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={completionPct}
                      onChange={(e) => setCompletionPct(e.target.value)}
                      className="w-24 rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                      data-testid={`resolve-pct-${d.id}`}
                    />
                    <span className="text-xs text-brand-500 self-center">
                      % selesai → refund {prorationFor(parseFloat(completionPct) || 0)}% ke pelanggan, sisa ke pekerja
                    </span>
                    <button
                      onClick={() => handleResolve(d.id, "partial")}
                      className="btn flex-1 bg-brand-600 text-white hover:bg-brand-700"
                      disabled={actionLoading || !resolveNote.trim()}
                      data-testid={`resolve-partial-${d.id}`}
                    >
                      Refund Sebagian
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SkckTab() {
  const [pendingSkck, setPendingSkck] = useState([]);
  const [expiredSkck, setExpiredSkck] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/api/admin/kyc/skck-pending"),
      api.get("/api/admin/kyc/skck-expired"),
    ])
      .then(([pendingRes, expiredRes]) => {
        setPendingSkck(pendingRes.data || []);
        setExpiredSkck(expiredRes.data || []);
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  return (
    <div className="animate-fade-up">
      <TabNav />
      <TabHeader title="Monitoring SKCK" sub="KYC yang perlu tindak lanjut terkait SKCK" />

      {error && <Alert kind="error" className="mb-4">{error}</Alert>}

      <div className="space-y-6">
        {/* SKCK Pending - Belum upload */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-orange-500" />
            <h2 className="text-lg font-bold text-brand-800">Belum Upload SKCK ({pendingSkck.length})</h2>
          </div>
          <p className="text-sm text-brand-400 mb-4">KYC sudah disetujui tapi worker belum upload SKCK</p>

          {pendingSkck.length === 0 ? (
            <div className="card p-6 text-center">
              <CheckCircle2 className="h-8 w-8 text-success-400 mx-auto mb-2" />
              <p className="text-sm text-brand-400">Semua worker sudah upload SKCK</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingSkck.map((item) => (
                <div key={item.id} className="card p-4 border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-brand-800">{item.worker_display_name || "Unknown"}</p>
                      <p className="text-sm text-brand-400">ID: {item.id?.slice(0, 8)}</p>
                      <p className="text-xs text-brand-400 mt-1">
                        Diajukan: {item.submitted_at ? new Date(item.submitted_at).toLocaleDateString("id-ID") : "-"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="chip bg-orange-100 text-orange-600">
                        <FileText className="h-3.5 w-3.5" /> SKCK Pending
                      </span>
                      <Link
                        to={`/admin/kyc?review=${item.id}`}
                        className="btn-primary !py-1.5 !px-3 text-sm"
                      >
                        Review
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SKCK Expired */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <h2 className="text-lg font-bold text-brand-800">SKCK Kadaluarsa ({expiredSkck.length})</h2>
          </div>
          <p className="text-sm text-brand-400 mb-4">KYC sudah disetujui tapi SKCK sudah kadaluarsa</p>

          {expiredSkck.length === 0 ? (
            <div className="card p-6 text-center">
              <CheckCircle2 className="h-8 w-8 text-success-400 mx-auto mb-2" />
              <p className="text-sm text-brand-400">Tidak ada SKCK kadaluarsa</p>
            </div>
          ) : (
            <div className="space-y-3">
              {expiredSkck.map((item) => (
                <div key={item.id} className="card p-4 border-red-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-brand-800">{item.worker_display_name || "Unknown"}</p>
                      <p className="text-sm text-brand-400">ID: {item.id?.slice(0, 8)}</p>
                      <p className="text-xs text-red-500 mt-1">
                        Kadaluarsa: {item.skck_expiry ? new Date(item.skck_expiry).toLocaleDateString("id-ID") : "-"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="chip bg-red-100 text-red-600">
                        <AlertTriangle className="h-3.5 w-3.5" /> Expired
                      </span>
                      <Link
                        to={`/admin/kyc?review=${item.id}`}
                        className="btn-primary !py-1.5 !px-3 text-sm"
                      >
                        Detail
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const PAYOUT_STATUS_STYLE = {
  PENDING: "bg-amber-100 text-amber-700",
  PROCESSING: "bg-blue-100 text-blue-700",
  COMPLETED: "bg-green-100 text-green-700",
  REJECTED: "bg-red-100 text-red-700",
};

function PromoTab() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    code: "",
    discount_type: "PERCENTAGE",
    discount_value: "",
    max_discount: "",
    min_amount: "",
    max_uses: "",
    per_user_limit: "",
    audience: "ALL",
    worker_tiers: [],
    funding: "FEE_CUT",
  });
  const TIERS = ["LITE", "ELITE", "PRO"];

  const fetchPromos = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/promos");
      setPromos(data || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromos();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = {
        code: form.code,
        discount_type: form.discount_type,
        discount_value: parseInt(form.discount_value, 10),
        min_amount: parseInt(form.min_amount || "0", 10),
      };
      if (form.max_discount) payload.max_discount = parseInt(form.max_discount, 10);
      if (form.max_uses) payload.max_uses = parseInt(form.max_uses, 10);
      if (form.per_user_limit) payload.per_user_limit = parseInt(form.per_user_limit, 10);
      payload.audience = form.audience;
      payload.funding = form.funding;
      if (form.audience === "WORKER" && form.worker_tiers.length > 0) payload.worker_tiers = form.worker_tiers;
      const { data } = await api.post("/api/admin/promos", payload);
      setPromos((prev) => [data, ...prev]);
      setForm({ code: "", discount_type: "PERCENTAGE", discount_value: "", max_discount: "", min_amount: "", max_uses: "", per_user_limit: "", audience: "ALL", worker_tiers: [], funding: "FEE_CUT" });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (p) => {
    try {
      const { data } = await api.put(`/api/admin/promos/${p.id}`, { is_active: !p.is_active });
      setPromos((prev) => prev.map((x) => (x.id === p.id ? data : x)));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Hapus promo ${p.code}?`)) return;
    try {
      await api.delete(`/api/admin/promos/${p.id}`);
      setPromos((prev) => prev.filter((x) => x.id !== p.id));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const startEdit = (p) => {
    setError("");
    setEditingId(p.id);
    setEditForm({
      discount_type: p.discount_type,
      discount_value: p.discount_value,
      max_discount: p.max_discount ?? "",
      min_amount: p.min_amount ?? 0,
      max_uses: p.max_uses ?? "",
      per_user_limit: p.per_user_limit ?? "",
      audience: p.audience || "ALL",
      worker_tiers: p.worker_tiers || [],
      funding: p.funding || "FEE_CUT",
    });
  };

  const handleUpdate = async (p) => {
    setError("");
    try {
      const payload = {
        discount_type: editForm.discount_type,
        discount_value: parseInt(editForm.discount_value, 10),
        min_amount: parseInt(editForm.min_amount || "0", 10),
        audience: editForm.audience,
        funding: editForm.funding,
      };
      if (editForm.max_discount !== "" && editForm.max_discount !== null) payload.max_discount = parseInt(editForm.max_discount, 10);
      if (editForm.max_uses !== "" && editForm.max_uses !== null) payload.max_uses = parseInt(editForm.max_uses, 10);
      if (editForm.per_user_limit !== "" && editForm.per_user_limit !== null) payload.per_user_limit = parseInt(editForm.per_user_limit, 10);
      if (editForm.audience === "WORKER") payload.worker_tiers = editForm.worker_tiers;
      const { data } = await api.put(`/api/admin/promos/${p.id}`, payload);
      setPromos((prev) => prev.map((x) => (x.id === p.id ? data : x)));
      setEditingId(null);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="animate-fade-up">
      <TabNav />
      <TabHeader title="Kode Promo" sub="Promo fee: potong % fee customer & potongan admin pekerja. Harga jasa tidak berubah." />

      {error && <Alert kind="error" className="mb-4">{error}</Alert>}

      <form onSubmit={handleCreate} className="card p-4 mb-6">
        <p className="font-bold text-brand-800 text-sm mb-3">Buat promo baru</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="Kode (cth. HEMAT20)" className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm uppercase focus:border-brand-500 focus:outline-none" data-testid="promo-code" />
          <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
            className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
            <option value="PERCENTAGE">Persen (%)</option>
            <option value="FIXED">Nominal (Rp)</option>
          </select>
          <input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
            placeholder={form.discount_type === "PERCENTAGE" ? "cth. 50 (% fee)" : "cth. 25000 (Rp fee)"}
            className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" data-testid="promo-value" />
          <input type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })}
            placeholder="Maks. potongan Rp (opsional)" className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
          <input type="number" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })}
            placeholder="Min. order Rp (opsional)" className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
          <input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
            placeholder="Kuota global, cth. 1000 (opsional)" className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
          <input type="number" value={form.per_user_limit} onChange={(e) => setForm({ ...form, per_user_limit: e.target.value })}
            placeholder="Maks. pakai per user, cth. 2 (opsional)" className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
          <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}
            className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" data-testid="promo-audience">
            <option value="ALL">Semua user</option>
            <option value="CUSTOMER">Customer saja</option>
            <option value="WORKER">Worker saja</option>
          </select>
          <select value={form.funding} onChange={(e) => setForm({ ...form, funding: e.target.value })}
            className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" data-testid="promo-funding"
            title="FEE_CUT: potong % fee (tidak bisa nombok). SUBSIDY: potong harga, ditalangi platform.">
            <option value="FEE_CUT">Potong fee platform</option>
            <option value="SUBSIDY">Subsidi platform (talangin)</option>
          </select>
        </div>
        {form.audience === "WORKER" && (
          <div className="mt-3 flex items-center gap-4 text-sm text-brand-700">
            <span className="font-semibold">Tier worker:</span>
            {TIERS.map((t) => (
              <label key={t} className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={form.worker_tiers.includes(t)}
                  onChange={(e) => setForm({
                    ...form,
                    worker_tiers: e.target.checked
                      ? [...form.worker_tiers, t]
                      : form.worker_tiers.filter((x) => x !== t),
                  })} />
                {t}
              </label>
            ))}
            <span className="text-xs text-brand-400">(kosong = semua tier)</span>
          </div>
        )}
        <button type="submit" className="btn-primary mt-3" data-testid="promo-create">Buat Promo</button>
      </form>

      <div className="space-y-3">
        {promos.length === 0 && (
          <div className="card p-6 text-center text-sm text-brand-400">Belum ada promo.</div>
        )}
        {promos.map((p) => (
          <div key={p.id} className="card p-4 gap-3">
            <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-extrabold text-brand-800">
                {p.code}
                <span className="ml-2 text-xs font-semibold text-brand-500">
                  {p.discount_type === "PERCENTAGE" ? `${p.discount_value}%` : `Rp${(p.discount_value || 0).toLocaleString("id-ID")}`}
                  {p.max_discount ? ` (maks Rp${p.max_discount.toLocaleString("id-ID")})` : ""}
                </span>
              </p>
              <p className="text-xs text-brand-400">
                dipakai {p.used_count}{p.max_uses ? `/${p.max_uses}` : ""} • min Rp{(p.min_amount || 0).toLocaleString("id-ID")}
                {p.per_user_limit ? ` • maks ${p.per_user_limit}x/user` : ""}
                {` • ${(p.audience || "ALL") === "ALL" ? "semua user" : (p.audience === "CUSTOMER" ? "customer saja" : `worker${p.worker_tiers?.length ? ` (${p.worker_tiers.join("/")})` : " (semua tier)"}`)}`}
                {` • ${(p.funding || "FEE_CUT") === "SUBSIDY" ? "subsidi platform" : "potong fee"}`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => toggleActive(p)}
                className={`chip ${p.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                data-testid={`promo-toggle-${p.id}`}>
                {p.is_active ? "Aktif" : "Mati"}
              </button>
              {editingId === p.id ? (
                <button onClick={() => setEditingId(null)} className="text-xs text-brand-500 hover:underline">
                  Batal
                </button>
              ) : (
                <button onClick={() => startEdit(p)} className="text-xs text-brand-600 hover:underline" data-testid={`promo-edit-${p.id}`}>
                  Ubah
                </button>
              )}
              <button onClick={() => handleDelete(p)} className="text-xs text-red-600 hover:underline">
                Hapus
              </button>
            </div>
            </div>
            {editingId === p.id && (
              <div className="mt-3 pt-3 border-t border-brand-50 grid sm:grid-cols-3 gap-2">
                <select value={editForm.discount_type} onChange={(e) => setEditForm({ ...editForm, discount_type: e.target.value })}
                  className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
                  <option value="PERCENTAGE">Persen (%)</option>
                  <option value="FIXED">Nominal (Rp)</option>
                </select>
                <input type="number" value={editForm.discount_value} onChange={(e) => setEditForm({ ...editForm, discount_value: e.target.value })}
                  placeholder="Nilai diskon" title="Nilai diskon"
                  className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
                <input type="number" value={editForm.max_discount} onChange={(e) => setEditForm({ ...editForm, max_discount: e.target.value })}
                  placeholder="Maks. potongan Rp" title="Maks. potongan Rp"
                  className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
                <input type="number" value={editForm.min_amount} onChange={(e) => setEditForm({ ...editForm, min_amount: e.target.value })}
                  placeholder="Min. order Rp" title="Min. order Rp"
                  className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
                <input type="number" value={editForm.max_uses} onChange={(e) => setEditForm({ ...editForm, max_uses: e.target.value })}
                  placeholder="Kuota global" title="Kuota global"
                  className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
                <input type="number" value={editForm.per_user_limit} onChange={(e) => setEditForm({ ...editForm, per_user_limit: e.target.value })}
                  placeholder="Maks/user" title="Maks. pakai per user"
                  className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
                <select value={editForm.audience} onChange={(e) => setEditForm({ ...editForm, audience: e.target.value })}
                  className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
                  <option value="ALL">Semua user</option>
                  <option value="CUSTOMER">Customer saja</option>
                  <option value="WORKER">Worker saja</option>
                </select>
                <select value={editForm.funding} onChange={(e) => setEditForm({ ...editForm, funding: e.target.value })}
                  className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
                  <option value="FEE_CUT">Potong fee platform</option>
                  <option value="SUBSIDY">Subsidi platform</option>
                </select>
                {editForm.audience === "WORKER" && (
                  <div className="flex items-center gap-3 text-sm text-brand-700 sm:col-span-2">
                    <span className="font-semibold">Tier:</span>
                    {TIERS.map((t) => (
                      <label key={t} className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={(editForm.worker_tiers || []).includes(t)}
                          onChange={(e) => setEditForm({
                            ...editForm,
                            worker_tiers: e.target.checked
                              ? [...(editForm.worker_tiers || []), t]
                              : (editForm.worker_tiers || []).filter((x) => x !== t),
                          })} />
                        {t}
                      </label>
                    ))}
                  </div>
                )}
                <div className="sm:col-span-3">
                  <button onClick={() => handleUpdate(p)} className="btn-primary" data-testid={`promo-save-${p.id}`}>Simpan Perubahan</button>
                  {p.used_count > 0 && (
                    <p className="text-xs text-brand-400 mt-1">Sudah dipakai {p.used_count}x — perubahan hanya berlaku untuk pemakaian berikutnya, riwayat lama tidak berubah.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const EMPTY_BANNER = {
  title: "", subtitle: "", link_url: "", audience: "ALL", kind: "TEXT", sort_order: 0,
  valid_from: "", valid_until: "", is_active: true,
};

function bannerSrc(url) {
  if (!url) return null;
  if (/^https?:\/\//.test(url)) return url;
  return `${BACKEND_URL}${url}`;
}

function BannerTab() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY_BANNER);
  const [formFile, setFormFile] = useState(null);
  const [formPreview, setFormPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editFile, setEditFile] = useState(null);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/banners");
      setBanners(data || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBanners(); }, []);

  const payloadFrom = (f) => ({
    title: f.title || "",
    subtitle: f.subtitle || null,
    link_url: f.link_url || null,
    audience: f.audience,
    kind: f.kind || "TEXT",
    sort_order: parseInt(f.sort_order || "0", 10),
    is_active: !!f.is_active,
    valid_from: f.valid_from ? new Date(f.valid_from).toISOString() : null,
    valid_until: f.valid_until ? new Date(f.valid_until).toISOString() : null,
  });

  const uploadImage = async (bannerId, file) => {
    const fd = new FormData();
    fd.append("image", file);
    // Timeout 60 dtk: upload pertama memuat model moderasi NSFW (~15 dtk).
    const { data } = await api.post(`/api/admin/banners/${bannerId}/image`, fd, { timeout: 60000 });
    return data;
  };

  const pickFormFile = (file) => {
    if (formPreview) URL.revokeObjectURL(formPreview);
    setFormFile(file || null);
    setFormPreview(file ? URL.createObjectURL(file) : null);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    if (form.kind === "IMAGE" && !formFile) {
      setError("Banner gambar wajib punya file gambar. Pilih gambar dulu.");
      return;
    }
    try {
      let { data } = await api.post("/api/admin/banners", payloadFrom(form));
      if (formFile) data = await uploadImage(data.id, formFile);
      setBanners((prev) => [data, ...prev]);
      setForm(EMPTY_BANNER);
      pickFormFile(null);
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail));
    }
  };

  const toggleActive = async (b) => {
    try {
      const { data } = await api.put(`/api/admin/banners/${b.id}`, { is_active: !b.is_active });
      setBanners((prev) => prev.map((x) => (x.id === b.id ? data : x)));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  const startEdit = (b) => {
    setError("");
    setEditingId(b.id);
    setEditFile(null);
    setEditForm({
      title: b.title || "",
      subtitle: b.subtitle || "",
      link_url: b.link_url || "",
      audience: b.audience || "ALL",
      kind: b.kind || "TEXT",
      sort_order: b.sort_order || 0,
      valid_from: toLocalInput(b.valid_from),
      valid_until: toLocalInput(b.valid_until),
      is_active: !!b.is_active,
    });
  };

  const handleUpdate = async (b) => {
    setError("");
    try {
      let { data } = await api.put(`/api/admin/banners/${b.id}`, payloadFrom(editForm));
      if (editFile) data = await uploadImage(b.id, editFile);
      setBanners((prev) => prev.map((x) => (x.id === b.id ? data : x)));
      setEditingId(null);
      setEditFile(null);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Hapus banner "${b.title || "tanpa judul"}"?`)) return;
    try {
      await api.delete(`/api/admin/banners/${b.id}`);
      setBanners((prev) => prev.filter((x) => x.id !== b.id));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  const handleUpload = async (file, b) => {
    if (!file) return;
    setError("");
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await api.post(`/api/admin/banners/${b.id}/image`, fd, { timeout: 60000 });
      setBanners((prev) => prev.map((x) => (x.id === b.id ? data : x)));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  const handleRemoveImage = async (b) => {
    if (!window.confirm("Hapus gambar banner ini?")) return;
    try {
      await api.delete(`/api/admin/banners/${b.id}/image`);
      const { data } = await api.get("/api/admin/banners");
      setBanners(data || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="animate-fade-up">
      <TabNav />
      <TabHeader title="Banner Program" sub="Banner untuk pekerja & pelanggan. Upload gambar, atur target audience, urutan, dan jadwal tayang." />

      {error && <Alert kind="error" className="mb-4">{error}</Alert>}

      <form onSubmit={handleCreate} className="card p-4 mb-6">
        <p className="font-bold text-brand-800 text-sm mb-3">Buat banner baru</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
            className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" data-testid="banner-kind">
            <option value="TEXT">Banner teks (pengumuman)</option>
            <option value="IMAGE">Banner gambar (image carousel)</option>
          </select>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={form.kind === "IMAGE" ? "Judul/caption (opsional)" : "Judul banner (cth. Pelatihan gratis minggu ini)"}
            required={form.kind === "TEXT"}
            className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" data-testid="banner-title" />
          <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
            placeholder="Subjudul / keterangan singkat"
            className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" data-testid="banner-subtitle" />
          <input value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })}
            placeholder="Link tujuan: /worker/finance atau https://..."
            className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" data-testid="banner-link" />
          <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}
            className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" data-testid="banner-audience">
            <option value="ALL">Semua user</option>
            <option value="WORKER">Pekerja saja</option>
            <option value="CUSTOMER">Pelanggan saja</option>
          </select>
          <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
            placeholder="Urutan tayang (kecil dulu)" title="Urutan tayang (kecil dulu)"
            className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" data-testid="banner-sort" />
          <label className="flex items-center gap-2 text-sm text-brand-700 px-1">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Langsung aktif
          </label>
          <label className="text-xs text-brand-500 flex flex-col gap-1">
            <span>Tayang mulai (opsional)</span>
            <input type="datetime-local" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
              className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" data-testid="banner-valid-from" />
          </label>
          <label className="text-xs text-brand-500 flex flex-col gap-1">
            <span>Tayang sampai (opsional)</span>
            <input type="datetime-local" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
              className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" data-testid="banner-valid-until" />
          </label>
          {form.kind === "IMAGE" && (
            <label className="sm:col-span-2 text-xs text-brand-500 flex flex-col gap-1">
              <span>
                Gambar banner (wajib untuk tipe gambar) — JPG/PNG/WebP, maks 2 MB.
                Rasio wajib <b>2:1</b>: buat di Canva dengan custom size <b>1000×500</b> lalu ekspor JPG/PNG.
                Otomatis difilter: konten dewasa/SARA/kekerasan ditolak.
              </span>
              <input type="file" accept="image/jpeg,image/png,image/webp"
                onChange={(e) => { const f = e.target.files?.[0] || null; e.target.value = ""; pickFormFile(f); }}
                className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm bg-white focus:border-brand-500 focus:outline-none" data-testid="banner-image-input" />
              {formPreview && (
                <div className="mt-2 w-full aspect-[2/1] rounded-xl border border-brand-100 overflow-hidden">
                  <img src={formPreview} alt="Pratinjau banner" className="h-full w-full object-cover" />
                </div>
              )}
            </label>
          )}
        </div>

        {(form.title || form.subtitle) && (
          <div className="mt-3 rounded-3xl overflow-hidden bg-gradient-to-r from-brand-700 via-brand-600 to-brand-500 text-white p-4">
            <p className="text-xs text-white/70 mb-1">Pratinjau teks (gambar diunggah setelah dibuat)</p>
            <p className="font-extrabold">{form.title || "Judul banner"}</p>
            {form.subtitle && <p className="text-sm text-white/85 mt-1">{form.subtitle}</p>}
          </div>
        )}

        <button type="submit" className="btn-primary mt-3" data-testid="banner-create">Buat Banner</button>
      </form>

      <div className="space-y-3">
        {banners.length === 0 && (
          <div className="card p-6 text-center text-sm text-brand-400">Belum ada banner.</div>
        )}
        {banners.map((b) => (
          <div key={b.id} className="card p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="h-16 w-24 rounded-xl overflow-hidden bg-gradient-to-br from-brand-700 to-brand-500 flex-shrink-0 grid place-items-center">
                  {b.image_url ? (
                    <img src={bannerSrc(b.image_url)} alt="" className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <Megaphone className="h-6 w-6 text-white/80" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-extrabold text-brand-800 flex items-center gap-2">
                    {b.title || "(tanpa judul)"}
                    <span className={`chip text-[10px] ${b.kind === "IMAGE" ? "bg-pink-50 text-pink-600" : "bg-brand-50 text-brand-500"}`}>
                      {b.kind === "IMAGE" ? "Gambar" : "Teks"}
                    </span>
                  </p>
                  {b.subtitle && <p className="text-sm text-brand-500">{b.subtitle}</p>}
                  <p className="text-xs text-brand-400 mt-1">
                    {b.audience === "ALL" ? "Semua user" : b.audience === "WORKER" ? "Pekerja" : "Pelanggan"}
                    {` • urutan ${b.sort_order}`}
                    {b.link_url ? ` • ${b.link_url}` : " • tanpa link"}
                    {(b.valid_from || b.valid_until) && (
                      <> • {b.valid_from ? new Date(b.valid_from).toLocaleString("id-ID") : "sekarang"} – {b.valid_until ? new Date(b.valid_until).toLocaleString("id-ID") : "seterusnya"}</>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                <button onClick={() => toggleActive(b)}
                  className={`chip ${b.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                  data-testid={`banner-toggle-${b.id}`}>
                  {b.is_active ? "Aktif" : "Mati"}
                </button>
                <button onClick={() => startEdit(b)} className="text-xs text-brand-600 hover:underline" data-testid={`banner-edit-${b.id}`}>
                  Ubah
                </button>
                <button onClick={() => handleDelete(b)} className="text-xs text-red-600 hover:underline" data-testid={`banner-delete-${b.id}`}>
                  Hapus
                </button>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-brand-50 flex items-center gap-3 flex-wrap">
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-brand-600 cursor-pointer chip bg-brand-50 hover:bg-brand-100" data-testid={`banner-upload-${b.id}`}>
                <ImagePlus className="h-4 w-4" /> {b.image_url ? "Ganti gambar" : "Upload gambar"}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleUpload(f, b); }} />
              </label>
              {b.image_url && (
                <button onClick={() => handleRemoveImage(b)} className="text-xs text-red-600 hover:underline" data-testid={`banner-image-remove-${b.id}`}>
                  Hapus gambar
                </button>
              )}
              <span className="text-xs text-brand-300">JPG/PNG/WebP, maks 2 MB, rasio 2:1 (Canva 1000×500). Tanpa gambar tetap tampil sebagai kartu teks.</span>
            </div>

            {editingId === b.id && (
              <div className="mt-3 pt-3 border-t border-brand-50 grid sm:grid-cols-2 gap-2">
                <select value={editForm.kind || "TEXT"} onChange={(e) => setEditForm({ ...editForm, kind: e.target.value })}
                  className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
                  <option value="TEXT">Banner teks</option>
                  <option value="IMAGE">Banner gambar</option>
                </select>
                <input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder={editForm.kind === "IMAGE" ? "Judul/caption (opsional)" : "Judul"}
                  className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
                <input value={editForm.subtitle} onChange={(e) => setEditForm({ ...editForm, subtitle: e.target.value })}
                  placeholder="Subjudul" className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
                <input value={editForm.link_url} onChange={(e) => setEditForm({ ...editForm, link_url: e.target.value })}
                  placeholder="Link tujuan" className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
                <select value={editForm.audience} onChange={(e) => setEditForm({ ...editForm, audience: e.target.value })}
                  className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
                  <option value="ALL">Semua user</option>
                  <option value="WORKER">Pekerja saja</option>
                  <option value="CUSTOMER">Pelanggan saja</option>
                </select>
                <input type="number" value={editForm.sort_order} onChange={(e) => setEditForm({ ...editForm, sort_order: e.target.value })}
                  placeholder="Urutan" title="Urutan tayang"
                  className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
                <label className="flex items-center gap-2 text-sm text-brand-700 px-1">
                  <input type="checkbox" checked={editForm.is_active} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} />
                  Aktif
                </label>
                <label className="text-xs text-brand-500 flex flex-col gap-1">
                  <span>Tayang mulai</span>
                  <input type="datetime-local" value={editForm.valid_from} onChange={(e) => setEditForm({ ...editForm, valid_from: e.target.value })}
                    className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
                </label>
                <label className="text-xs text-brand-500 flex flex-col gap-1">
                  <span>Tayang sampai</span>
                  <input type="datetime-local" value={editForm.valid_until} onChange={(e) => setEditForm({ ...editForm, valid_until: e.target.value })}
                    className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
                </label>
                <label className="sm:col-span-2 text-xs text-brand-500 flex flex-col gap-1">
                  <span>Ganti gambar (opsional; kosongkan bila tidak ingin mengganti)</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => { const f = e.target.files?.[0] || null; e.target.value = ""; setEditFile(f); }}
                    className="rounded-xl border-2 border-brand-100 px-3 py-2 text-sm bg-white focus:border-brand-500 focus:outline-none" />
                </label>
                <div className="sm:col-span-2 flex items-center gap-3">
                  <button onClick={() => handleUpdate(b)} className="btn-primary" data-testid={`banner-save-${b.id}`}>Simpan Perubahan</button>
                  <button type="button" onClick={() => setEditingId(null)} className="text-xs text-brand-500 hover:underline">Batal</button>
                  <span className="text-xs text-brand-300 flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Kosongkan jadwal = tayang terus.</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FinanceTab() {
  const [revenue, setRevenue] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [stuck, setStuck] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [adminNote, setAdminNote] = useState("");
  const [refundMsg, setRefundMsg] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [rev, pay, led, stk] = await Promise.all([
        api.get("/api/finance/admin/revenue"),
        api.get("/api/finance/admin/payouts"),
        api.get("/api/finance/admin/ledger", { params: { limit: 20 } }),
        api.get("/api/finance/admin/payments", { params: { stuck: true } }),
      ]);
      setRevenue(rev.data);
      setPayouts(pay.data || []);
      setLedger(led.data || []);
      setStuck(stk.data || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleProcess = async (id, action) => {
    if ((action === "reject") && !adminNote.trim()) return;
    setActionLoading(true);
    setError("");
    try {
      const { data } = await api.post(`/api/finance/admin/payouts/${id}/process`, {
        action,
        admin_note: adminNote || null,
      });
      setPayouts((prev) => prev.map((p) => (p.id === id ? data : p)));
      setAdminNote("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <Spinner />;

  const pending = payouts.filter((p) => p.status === "PENDING" || p.status === "PROCESSING");
  const done = payouts.filter((p) => p.status === "COMPLETED" || p.status === "REJECTED");

  const handleRefund = async (paymentId) => {
    if (!window.confirm("Refund penuh dana ini ke pelanggan? Aksi tercatat di audit.")) return;
    setActionLoading(true);
    setRefundMsg("");
    setError("");
    try {
      await api.post(`/api/payments/${paymentId}/refund`);
      setStuck((prev) => prev.filter((p) => p.id !== paymentId));
      setRefundMsg("Refund berhasil. Dana kembali ke pelanggan.");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="animate-fade-up">
      <TabNav />
      <TabHeader title="Keuangan" sub="Ledger immutable, pendapatan platform, dan antrean penarikan dana" />

      {error && <Alert kind="error" className="mb-4">{error}</Alert>}
      {refundMsg && <Alert kind="success" className="mb-4">{refundMsg}</Alert>}

      {/* Intervensi manual: dana nyangkut */}
      <div className={`card p-5 mb-6 ${stuck.length > 0 ? "border-red-300 bg-red-50/50" : ""}`} data-testid="stuck-payments">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-brand-800">
              Dana Nyangkut {stuck.length > 0 && <span className="chip bg-red-500 text-white ml-2">{stuck.length} perlu tindakan</span>}
            </h2>
            <p className="text-sm text-brand-400 mt-0.5">
              Payment aktif di pesanan batal/ditolak. Sweeper otomatis refund tiap menit — tombol di bawah untuk intervensi SEGERA tanpa menunggu.
            </p>
          </div>
          <button onClick={fetchAll} className="btn-ghost text-sm flex-shrink-0" disabled={actionLoading}>
            <RefreshCw className="h-4 w-4" /> Cek ulang
          </button>
        </div>
        {stuck.length === 0 ? (
          <p className="text-sm text-success-600 mt-3 flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4" /> Bersih — tidak ada dana nyangkut.
          </p>
        ) : (
          <div className="mt-3 space-y-2">
            {stuck.map((p) => (
              <div key={p.id} className="rounded-xl bg-white border border-red-200 p-3 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[180px]">
                  <p className="font-bold text-brand-800 text-sm">
                    Rp{(p.amount || 0).toLocaleString("id-ID")} • {p.customer_display_name || "Pelanggan"} → {p.worker_display_name || "Pekerja"}
                  </p>
                  <p className="text-xs text-brand-400 mt-0.5">
                    Payment {p.status} • Booking {p.booking_status} • {p.id.slice(0, 8)}
                  </p>
                </div>
                {p.can_refund ? (
                  <button
                    onClick={() => handleRefund(p.id)}
                    disabled={actionLoading}
                    className="btn-primary text-sm !py-2"
                    data-testid={`refund-stuck-${p.id}`}
                  >
                    Refund Sekarang
                  </button>
                ) : (
                  <span className="text-xs text-brand-400">Selesaikan via putusan sengketa</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Revenue */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card p-5">
          <p className="text-sm text-brand-400">Pendapatan Platform</p>
          <p className="text-xl font-extrabold text-brand-800 mt-1">
            Rp{(revenue?.platform_fee_total || 0).toLocaleString("id-ID")}
          </p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-brand-400">Saldo Escrow</p>
          <p className="text-xl font-extrabold text-brand-800 mt-1">
            Rp{(revenue?.escrow_balance || 0).toLocaleString("id-ID")}
          </p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-brand-400">Total ke Pekerja</p>
          <p className="text-xl font-extrabold text-brand-800 mt-1">
            Rp{(revenue?.worker_earnings_total || 0).toLocaleString("id-ID")}
          </p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-brand-400">Penarikan Selesai</p>
          <p className="text-xl font-extrabold text-brand-800 mt-1">
            Rp{(revenue?.payouts_completed_total || 0).toLocaleString("id-ID")}
          </p>
        </div>
      </div>

      {/* Payout queue */}
      <h2 className="text-lg font-bold text-brand-800 mb-3">
        Antrean Penarikan ({pending.length})
      </h2>
      {pending.length === 0 ? (
        <div className="card p-6 text-center mb-6">
          <CheckCircle2 className="h-8 w-8 text-success-400 mx-auto mb-2" />
          <p className="text-sm text-brand-400">Tidak ada penarikan menunggu</p>
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          {pending.map((p) => (
            <div key={p.id} className="card p-4 border-amber-200">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <p className="font-bold text-brand-800">{p.worker_display_name || "Pekerja"}</p>
                  <p className="text-sm text-brand-500 font-extrabold">
                    Rp{(p.amount || 0).toLocaleString("id-ID")}
                  </p>
                  <p className="text-xs text-brand-400 break-words">Rek: {p.bank_account}</p>
                  <p className="text-xs text-brand-300">
                    {p.created_at ? new Date(p.created_at).toLocaleString("id-ID") : ""}
                  </p>
                </div>
                <span className={`chip ${PAYOUT_STATUS_STYLE[p.status] || "bg-brand-50 text-brand-600"}`}>
                  {p.status}
                </span>
              </div>
              <input
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Catatan admin (wajib untuk tolak)"
                className="w-full rounded-xl border-2 border-brand-100 px-3 py-2 text-sm mb-2 focus:border-brand-500 focus:outline-none"
              />
              <div className="flex gap-2">
                {p.status === "PENDING" && (
                  <button
                    onClick={() => handleProcess(p.id, "process")}
                    className="btn flex-1 bg-blue-500 text-white hover:bg-blue-600"
                    disabled={actionLoading}
                  >
                    Proses
                  </button>
                )}
                <button
                  onClick={() => handleProcess(p.id, "complete")}
                  className="btn-success flex-1"
                  disabled={actionLoading}
                >
                  <CheckCircle2 className="h-4 w-4" /> Selesai
                </button>
                <button
                  onClick={() => handleProcess(p.id, "reject")}
                  className="btn-ghost flex-1 border-red-200 text-red-600"
                  disabled={actionLoading || !adminNote.trim()}
                >
                  <XCircle className="h-4 w-4" /> Tolak
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* History */}
      {done.length > 0 && (
        <>
          <h2 className="text-lg font-bold text-brand-800 mb-3">Riwayat Penarikan</h2>
          <div className="card p-2 mb-6">
            <ul className="divide-y divide-brand-50">
              {done.slice(0, 10).map((p) => (
                <li key={p.id} className="flex items-center justify-between px-3 py-2.5 gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-brand-800 truncate">
                      {p.worker_display_name || "Pekerja"}
                    </p>
                    <p className="text-xs text-brand-400">
                      Rp{(p.amount || 0).toLocaleString("id-ID")}
                      {p.admin_note ? ` • ${p.admin_note}` : ""}
                    </p>
                  </div>
                  <span className={`chip ${PAYOUT_STATUS_STYLE[p.status] || ""}`}>{p.status}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {/* Ledger */}
      <h2 className="text-lg font-bold text-brand-800 mb-3">Ledger Terakhir (append-only)</h2>
      <div className="card p-2 overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-left text-brand-400 text-xs">
              <th className="px-3 py-2">Waktu</th>
              <th className="px-3 py-2">Tipe</th>
              <th className="px-3 py-2">Akun</th>
              <th className="px-3 py-2 text-right">Jumlah</th>
              <th className="px-3 py-2">Keterangan</th>
            </tr>
          </thead>
          <tbody>
            {ledger.map((r) => (
              <tr key={r.id} className="border-t border-brand-50">
                <td className="px-3 py-2 text-xs text-brand-500 whitespace-nowrap">
                  {r.created_at ? new Date(r.created_at).toLocaleString("id-ID") : ""}
                </td>
                <td className="px-3 py-2">
                  <span className={`chip ${r.entry_type === "DEBIT" ? "bg-red-50 text-red-600" : "bg-green-50 text-green-700"}`}>
                    {r.entry_type}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-brand-700">{r.account}</td>
                <td className="px-3 py-2 text-right font-bold text-brand-800">
                  Rp{(r.amount || 0).toLocaleString("id-ID")}
                </td>
                <td className="px-3 py-2 text-xs text-brand-500 max-w-[220px] truncate">{r.description || ""}</td>
              </tr>
            ))}
            {ledger.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-sm text-brand-400">
                  Belum ada entri ledger. Ledger terisi otomatis saat ada settlement (hold/release/refund).
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
